import { http, API_BASE_URL_TEST, API_BASE_URL_PROD, getError, catchError } from "services/http"
import i18next from "i18next"
import { isNotEmpty } from "utils/validation"
import { ErrorFn, Bike, ErrorResponse, BikeRequestingTranfer } from "models"

const getBike = async (bikeId: string, mode: string): Promise<{ res: Bike } & ErrorFn> => {
	const getBikeErrors = (error: ErrorResponse) => {
		switch (error?.code) {
			case "BikeNotFoundException":
				return {
					code: error?.code,
					message: i18next.t("search_error_BikeNotFoundException", { ns: "api" }),
				}
			case "InvalidArgument":
				return {
					code: error?.code,
					message: i18next.t("search_error_InvalidArgument", { ns: "api" }),
				}
			case "NotAuthorizedException":
			case "EntityStatusException":
				return {
					code: error?.code,
					message: i18next.t("sell_error_NotAuthorizedException", { ns: "api" }),
				}
			default:
				return {
					code: error?.code,
					message: i18next.t("search_error_default", { ns: "api" }),
				}
		}
	}

	let param = ""
	switch (mode) {
		case "byBikeId":
			param = `bike_id=${bikeId}`
			break
		case "byOcodeId":
			param = `ocode_id=${bikeId}`
			break
		default:
			param = `bike_id=${bikeId}`
			break
	}

	const res = await http
		.get<Bike>(`obikeapi/auth_store/bike?${param}`, {})
		.then((res) => res.data)
		.catch((e) => catchError(e))
	// console.log("getBike res: ", res)
	return {
		res: res as Bike,
		getError: getError(res as unknown as ErrorResponse, getBikeErrors),
	}
}

const getBikes = async (searchable: string, mode: string): Promise<{ res: Bike[] } & ErrorFn> => {
	const getBikesErrors = (error: ErrorResponse) => {
		switch (error?.code) {
			case "BikeNotFoundException":
				return {
					code: error?.code,
					message: i18next.t("search_error_BikeNotFoundException", { ns: "api" }),
				}
			case "InvalidArgument":
				return {
					code: error?.code,
					message: i18next.t("search_error_InvalidArgument", { ns: "api" }),
				}
			case "NotAuthorizedException":
			case "EntityStatusException":
				return {
					code: error?.code,
					message: i18next.t("sell_error_NotAuthorizedException", { ns: "api" }),
				}
			default:
				return {
					code: error?.code,
					message: i18next.t("search_error_default", { ns: "api" }),
				}
		}
	}
	let param = ""
	switch (mode) {
		case "mail":
			param = `mail=${searchable}`
			break
		case "phone":
			param = `phone=${encodeURIComponent(searchable)}`
			break
		default:
			param = `mail=${searchable}`
	}

	const res = await http
		.get(`obikeapi/auth_store/consumer/bikes?${param}`, {})
		.then((res) => res.data)
		.catch((e) => catchError(e))
	return {
		res: res as Bike[],
		getError: getError(res as unknown as ErrorResponse, getBikesErrors),
	}
}

const getActivatedBikesHistoryInfos = () => {
	const getActivatedBikesHistoryErrors = (error: ErrorResponse) => {
		switch (error?.code) {
			case "InvalidArgument":
				return {
					code: error?.code,
					message: i18next.t("search_error_InvalidArgument", { ns: "api" }),
				}
			case "NotAuthorizedException":
			case "EntityStatusException":
				return {
					code: error?.code,
					message: i18next.t("sell_error_NotAuthorizedException", { ns: "api" }),
				}
			default:
				return { code: error?.code, message: i18next.t("global_error", { ns: "api" }) }
		}
	}

	const url = "obikeapi/auth_store/bikes"

	const route =
		process.env.REACT_APP_MODE === "production"
			? API_BASE_URL_PROD + url
			: API_BASE_URL_TEST + url

	return {
		route,
		getError: getActivatedBikesHistoryErrors,
	}
}

const getPreRegistrationBikesHistoryInfos = () => {
	const getPreRegistrationBikesHistoryErrors = (error: ErrorResponse) => {
		switch (error?.code) {
			case "InvalidArgument":
				return {
					code: error?.code,
					message: i18next.t("search_error_InvalidArgument", { ns: "api" }),
				}
			case "NotAuthorizedException":
			case "EntityStatusException":
				return {
					code: error?.code,
					message: i18next.t("sell_error_NotAuthorizedException", { ns: "api" }),
				}
			default:
				return { code: error?.code, message: i18next.t("global_error", { ns: "api" }) }
		}
	}

	const url = "obikeapi/auth_store/bikes_descriptions"

	const route =
		process.env.REACT_APP_MODE === "production"
			? API_BASE_URL_PROD + url
			: API_BASE_URL_TEST + url

	return {
		route,
		getError: getPreRegistrationBikesHistoryErrors,
	}
}

const createBike = async (bike): Promise<{ res: Bike } & ErrorFn> => {
	const createBikeErrors = (error: ErrorResponse) => {
		switch (error?.code) {
			case "NotAuthorizedException":
				if (
					error?.message === "Operation is not allowed: role." ||
					error?.message === "Operation is not allowed: operateur."
				) {
					return {
						code: error?.code,
						message: i18next.t("sell_error_NotAuthorizedException", { ns: "api" }),
					}
				} else if (
					error?.message === "Access token is required." ||
					error?.message === "Invalid token specified"
				) {
					return {
						code: error?.code,
						message: i18next.t("sell_error_AccessTokenRequired", { ns: "api" }),
					}
				}
				break
			case "BikeNotFoundException":
				return {
					code: error?.code,
					message: i18next.t("search_error_BikeNotFoundException", { ns: "api" }),
				}
			case "InvalidArgument":
				return {
					code: error?.code,
					message: i18next.t("search_error_InvalidArgument", { ns: "api" }),
				}
			case "ConsumerNotFoundException":
				return {
					code: error?.code,
					message: i18next.t("sell_error_ConsumerNotFoundException", { ns: "api" }),
				}
			case "EntityStatusException":
				return {
					code: error?.code,
					message: i18next.t("sell_error_NotAuthorizedException", { ns: "api" }),
				}
			case "FnuciApiException":
				return {
					code: error?.code,
					message: i18next.t("create_global_error", { ns: "api" }),
				} // FIXME we must customize that error.
			default:
				return { code: error?.code, message: i18next.t("sell_global_error", { ns: "api" }) }
		}
	}

	const res = await http
		.post("obikeapi/auth_store/bike", bike, {})
		.then((res) => res.data)
		.catch((e) => catchError(e))
	// console.log("createBike res: ", res)
	return {
		res: res as Bike,
		getError: getError(res as unknown as ErrorResponse, createBikeErrors),
	}
}

const editBike = async (bike) => {
	const editBikeErrors = (error: ErrorResponse) => {
		switch (error?.code) {
			case "NotAuthorizedException":
				switch (error?.message) {
					case "Access token is required.":
						return {
							code: error?.code,
							message: i18next.t("bike_edit_global_error", { ns: "api" }),
						}
					case "Operation is not allowed: operateur.":
					case "Operation is not allowed: status.":
					case "Operation is not allowed: role.":
						return {
							code: error?.code,
							message: i18next.t("edit_error_NotAuthorizedException", { ns: "api" }),
						}
					case "Operation is not allowed: mail verified.":
						return {
							code: error?.code,
							message: i18next.t("edit_error_NotAuthorizedExceptionMailVerified", {
								ns: "bike",
							}),
						}
					case "Operation is not allowed: account is activated.":
						return {
							code: error?.code,
							message: i18next.t("edit_error_AccountIsActivated", { ns: "bike" }),
						}
					case "Operation is not allowed: you did not request a property transfer.":
						return {
							code: error?.code,
							message: i18next.t("edit_error_NoRequestPropertyTransfer", {
								ns: "bike",
							}),
						}
					case "Operation is not allowed: you are not the seller of this bike.":
						return {
							code: error?.code,
							message: i18next.t("edit_error_NotSellerBike", { ns: "bike" }),
						}
					case "Operation is not allowed: you have to wait for the legal duration.":
						return {
							code: error?.code,
							message: i18next.t("edit_error_TimeExpired", { ns: "bike" }),
						}
					default:
						break
				}
				break
			case "EntityStatusException":
				return {
					code: error?.code,
					message: i18next.t("edit_error_EntityStatusException", { ns: "form" }),
				}
			case "BikeNotFoundException":
				return {
					code: error?.code,
					message: i18next.t("search_error_BikeNotFoundException", { ns: "api" }),
				}
			case "TooManyRequestsException":
				return {
					code: error?.code,
					message: i18next.t("edit_error_TooManyRequestsException", { ns: "api" }),
				}
			case "BikeVersionMismatchException":
				return {
					code: error?.code,
					message: i18next.t("edit_error_BikeVersionMismatchException", { ns: "bike" }),
				}
			default:
				return { code: error?.code, message: i18next.t("global_error", { ns: "form" }) }
		}
	}
	const res = await http.put("obikeapi/auth_store/bike", bike).catch((e) => catchError(e))
	//const res = true
	// console.log("editBike res: ", res)
	return {
		res,
		getError: getError(res, editBikeErrors),
	}
}

const editBikeDescription = async (bike): Promise<{ res: any } & ErrorFn> => {
	const editBikeDescriptionErrors = (error: ErrorResponse) => {
		switch (error?.code) {
			case "NotAuthorizedException":
				switch (error?.message) {
					case "Access token is required.":
						return {
							code: error?.code,
							message: i18next.t("global_error", { ns: "form" }),
						}
					case "Operation is not allowed: operateur.":
						return {
							code: error?.code,
							message: i18next.t("edit_error_NotAuthorizedException", { ns: "api" }),
						}
					default:
						break
				}
				break

			case "EntityStatusException":
				return {
					code: error?.code,
					message: i18next.t("edit_error_EntityStatusException", { ns: "form" }),
				}

			case "InvalidArgument":
				return { code: error?.code, message: i18next.t("global.format_invalid") }

			case "DuplicateOcodeIdException":
			case "DuplicatePackingIdException":
				return {
					code: error?.code,
					message: i18next.t("bike_edit_global_error", { ns: "api" }),
				}

			case "BikeNotFoundException":
				return {
					code: error?.code,
					message: i18next.t("edit_error_BikeNotFoundException", { ns: "bike" }),
				}

			default:
				return { code: error?.code, message: i18next.t("global_error", { ns: "form" }) }
		}
	}
	const res = await http
		.put("obikeapi/auth_store/bike_description", bike)
		.then((res) => res.data)
		.catch((e) => catchError(e))
	// console.log("editBikeDescription res: ", res)
	return {
		res: res as any,
		getError: getError(res as unknown as ErrorResponse, editBikeDescriptionErrors),
	}
}

const updateBikeDescription = async (bike) => {
	const updateBikeDescriptionErrors = (error: ErrorResponse) => {
		switch (error?.code) {
			case "NotAuthorizedException":
				switch (error?.message) {
					case "Access token is required.":
						return {
							code: error?.code,
							message: i18next.t("global_error", { ns: "form" }),
						}
					case "Operation is not allowed: operateur.":
					case "Operation is not allowed: role.":
						return {
							code: error?.code,
							message: i18next.t("edit_error_NotAuthorizedException", { ns: "api" }),
						}
					default:
						break
				}
				break

			case "EntityStatusException":
				return {
					code: error?.code,
					message: i18next.t("edit_error_EntityStatusException", { ns: "form" }),
				}

			case "InvalidArgument":
				return { code: error?.code, message: i18next.t("global.format_invalid") }

			case "DuplicateOcodeIdException":
			case "DuplicatePackingIdException":
				return {
					code: error?.code,
					message: i18next.t("bike_edit_global_error", { ns: "api" }),
				}

			case "TooManyRequestsException":
				return {
					code: error?.code,
					message: i18next.t("edit_error_TooManyRequestsException", { ns: "api" }),
				}

			case "BikeNotFoundException":
				return {
					code: error?.code,
					message: i18next.t("edit_error_BikeNotFoundException", { ns: "bike" }),
				}
			default:
				return { code: error?.code, message: i18next.t("global_error", { ns: "form" }) }
		}
	}

	const res = 
		await http.put("obikeapi/auth_store/bike", bike)
		.then((res) => res.data)
		.catch((e) => catchError(e))
	return {
		res: res as any,
		getError: getError(res as unknown as ErrorResponse, updateBikeDescriptionErrors),
	}
}

const portabilityRequest = async (bike): Promise<{ res: any } & ErrorFn> => {
	const portabilityRequestErrors = (error: ErrorResponse) => {
		switch (error?.code) {
			case "NotAuthorizedException":
				switch (error?.message) {
					case "Access token is required.":
						return {
							code: error?.code,
							message: i18next.t("global_error", { ns: "form" }),
						}
					case "Operation is not allowed: operateur.":
						return {
							code: error?.code,
							message: i18next.t("edit_error_NotAuthorizedException", { ns: "api" }),
						}
					default:
						break
				}
				break

			case "EntityStatusException":
				return {
					code: error?.code,
					message: i18next.t("edit_error_EntityStatusException", { ns: "form" }),
				}

			case "EntityNotFoundException":
				return {
					code: error?.code,
					message: i18next.t("edit_errorEntityNotFoundException", { ns: "api" }),
				}

			case "FnuciApiException":
				switch (error?.message) {
					case "Bike_id not found.":
						return {
							code: error?.code,
							message: i18next.t("search_error_BikeNotFoundException", { ns: "api" }),
						}
					case "You try to transfer yourself a bike that is already managed by you.":
						return {
							code: error?.code,
							message: i18next.t("portabilityRequest_error_alreadyManaged", {
								ns: "api",
							}),
						}
					case "This bike_id is not yet registered.":
						return {
							code: error?.code,
							message: i18next.t("portabilityRequest_error_alreadyRegistered", {
								ns: "api",
							}),
						}
					case "Mail has already received a code but had not yet used it.":
						return {
							code: error?.code,
							message: i18next.t("portabilityRequest_error_codeSending", {
								ns: "api",
							}),
						}
					default:
						return {
							code: error?.code,
							message: i18next.t("edit_error_NotAuthorizedException", { ns: "api" }),
						}
				}

			case "BikeNotFoundException":
				return {
					code: error?.code,
					message: i18next.t("edit_error_BikeNotFoundException", { ns: "bike" }),
				}

			default:
				return { code: error?.code, message: i18next.t("global_error", { ns: "form" }) }
		}
	}
	const res = await http
		.post("obikeapi/auth_store/bike/portability", bike, {})
		.then((res) => res.data)
		.catch((e) => catchError(e))
	return {
		res: res as any,
		getError: getError(res as unknown as ErrorResponse, portabilityRequestErrors),
	}
}

const getBikePortability = async (bikeId: string, operator: string): Promise<{ res: BikeRequestingTranfer } & ErrorFn> => {
	const getBikeErrors = (error: ErrorResponse) => {
		switch (error?.code) {
			case "BikeNotFoundException":
				return {
					code: error?.code,
					message: i18next.t("search_error_BikeNotFoundException", { ns: "api" }),
				}
			case "InvalidArgument":
				return {
					code: error?.code,
					message: i18next.t("search_error_InvalidArgument", { ns: "api" }),
				}
			case "NotAuthorizedException":
			case "EntityStatusException":
				return {
					code: error?.code,
					message: i18next.t("sell_error_NotAuthorizedException", { ns: "api" }),
				}
			case "RessourceNotFoundException":
				return {
					code: error?.code,
					message: i18next.t("portability_RessourceNotFoundException_error", {
						ns: "api",
					}),
				}
			default:
				return {
					code: error?.code,
					message: i18next.t("search_error_default", { ns: "api" }),
				}
		}
	}

	const param = `bike_id=${bikeId}&operator=${operator}`

	const res = await http
		.get(`obikeapi/auth_store/bike/portability?${param}`, {})
		.then((res) => res.data)
		.catch((e) => catchError(e))
	return {
		res: res as BikeRequestingTranfer,
		getError: getError(res as unknown as ErrorResponse, getBikeErrors),
	}
}

type addBikeEventProps = {
	bikeId: string
	title: string
	comment: string
	date: string
	row_version: string
}

const addBikeEvent = async ({ bikeId, title, comment, date, row_version }: addBikeEventProps) => {
	const getAddBikeEventErrors = (error: ErrorResponse) => {
		switch (error?.code) {
			case "BikeNotFoundException":
				return {
					code: error?.code,
					message: i18next.t("search_error_BikeNotFoundException", { ns: "api" }),
				}
			case "InvalidArgument":
				return {
					code: error?.code,
					message: i18next.t("search_error_InvalidArgument", { ns: "api" }),
				}
			case "NotAuthorizedException":
			case "EntityStatusException":
				return {
					code: error?.code,
					message: i18next.t("sell_error_NotAuthorizedException", { ns: "api" }),
				}
			default:
				return { code: error?.code, message: i18next.t("global_error", { ns: "api" }) }
		}
	}

	const payload = {
		bike_id: bikeId,
		event_title: title,
		...(comment ? { event_comment: comment } : {}),
		event_date: date,
		row_version: isNotEmpty(row_version) ? row_version : "0",
	}

	const res = await http
		.post("obikeapi/auth_store/bike/event", payload, {})
		.then((res) => res.data)
		.catch((e) => catchError(e))
	return {
		res: res as any,
		getError: getError(res as unknown as ErrorResponse, getAddBikeEventErrors),
	}
}

export {
	getBike,
	getBikes,
	getActivatedBikesHistoryInfos,
	getPreRegistrationBikesHistoryInfos,
	createBike,
	editBike,
	editBikeDescription,
	updateBikeDescription,
	portabilityRequest,
	getBikePortability,
	addBikeEvent,
}
